import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Badge, Alert, Spinner } from "@vlab-corporation/design-system";
import { detectClassType, detectIsOt } from "@vlab-corporation/vlab-pilates-default-service/class";
import { AppLayout } from "@/components/AppLayout";
import { MemberTypeBadge } from "@/components/MemberTypeBadge";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { usePendingJournals, getMonthRange, get30DayRange } from "@/hooks/usePendingJournals";
import { resolveMyStaffKey } from "@/utils/staff-identity";
import type { AuthUser } from "@/types/auth";

function getClassTypeLabel(maxTrainees: number, title: string): string {
  const ct = maxTrainees > 0 ? detectClassType(maxTrainees) : "PRIVATE_REGULAR";
  const ot = detectIsOt(title);
  if (ct === "GROUP_REGULAR") return ot ? "그룹 OT" : "그룹 정규";
  return ot ? "1:1 OT" : "1:1 정규";
}

export function PendingJournalsPage() {
  const user = useAuthenticatedUser();
  if (!user) return null;
  return <PendingJournalsPageContent user={user} />;
}

function PendingJournalsPageContent({ user }: { user: AuthUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { centerOwnerUid, centerId } = user.activeMembership;
  const myStaffKey = resolveMyStaffKey(user.activeMembership);

  const month = searchParams.get("month");
  const dateRange = useMemo(() => (month ? getMonthRange(month) : get30DayRange()), [month]);

  const { pendingClasses, reRegistrations, isLoading, error, apiError } = usePendingJournals(
    centerOwnerUid,
    centerId,
    myStaffKey,
    dateRange,
  );
  const instructorId = user.activeMembership.instructorId;

  return (
    <AppLayout>
      <div className="mb-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          ← 뒤로
        </Button>
      </div>

      <h2 className="text-lg font-semibold mb-4">일지 미작성 수업 ({pendingClasses.length}건)</h2>

      {apiError && (
        <Alert variant="warning" className="mb-4">
          {apiError.message}
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : error ? (
        <Alert variant="error" className="mb-4">
          수업 정보를 불러오지 못했습니다.
        </Alert>
      ) : pendingClasses.length === 0 ? (
        <Card className="p-4 text-center text-gray-500">미작성 일지가 없습니다.</Card>
      ) : (
        <div className="space-y-3">
          {pendingClasses.map((c) => (
            <Card
              key={c.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() =>
                navigate(`/classes/${c.id}/journal?date=${c.date}&from=/pending-journals`)
              }
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={c.maxTrainees === 1 ? "info" : "warning"}>
                      {getClassTypeLabel(c.maxTrainees, c.title)}
                    </Badge>
                    <span className="font-medium text-sm">{c.title}</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {c.startTime} - {c.endTime}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{c.date}</span>
                  <span>
                    수강생 {c.currentTrainees}/{c.maxTrainees}명
                  </span>
                </div>
                {/* 수강생 목록 (cancel 상태 제외) */}
                {c.bookings.filter((b) => b.status !== "cancel").length > 0 && (
                  <div className="mt-2 space-y-1">
                    {c.bookings
                      .filter((b) => b.status !== "cancel")
                      .map((b, idx) => (
                        <div key={idx} className="flex items-center gap-1 text-xs text-gray-700">
                          <span>{b.memberName}</span>
                          <MemberTypeBadge
                            member={b.member}
                            classDate={c.date}
                            instructorId={instructorId}
                            reRegistrations={reRegistrations}
                          />
                          {b.maxCoupon != null && b.remainingCoupon != null && (
                            <span className="text-gray-400">
                              {b.maxCoupon - b.remainingCoupon}회차(잔여 {b.remainingCoupon}/
                              {b.maxCoupon})
                            </span>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
